import {
  BoardElement,
  CalloutElement,
  ColumnElement,
  CommentLeaf,
  ELEMENT_AI_CONTENT,
  ELEMENT_BOARD,
  ELEMENT_CALLOUT_TIP,
  ELEMENT_CODE_HIGHLIGHT_BLOCK,
  ELEMENT_COL,
  ELEMENT_DATE_TODAY,
  ELEMENT_EMBED,
  ELEMENT_FILE,
  ELEMENT_LEGAL_PH,
  ELEMENT_MICRO_TASK,
  ELEMENT_MULTI_COL,
  ELEMENT_TABLE_PLUS,
  EmbedElement,
  HrElement,
  ImageElement,
  LegalPhElement,
  LinkElement,
  MARK_COMMENT_ID,
  MicroTaskElement,
  MultiColumnElement,
  TablePlusElement,
  TodoListElement,
  withDraggables,
} from '@sketchnote/slate-plugins';
import {
  BlockquoteElement,
  createPlateUI,
  ELEMENT_BLOCKQUOTE,
  ELEMENT_CODE_BLOCK,
  ELEMENT_H1,
  ELEMENT_H2,
  ELEMENT_H3,
  ELEMENT_H4,
  ELEMENT_H5,
  ELEMENT_H6,
  ELEMENT_HR,
  ELEMENT_IMAGE,
  ELEMENT_LINK,
  ELEMENT_MEDIA_EMBED,
  ELEMENT_MENTION,
  ELEMENT_OL,
  ELEMENT_PARAGRAPH,
  ELEMENT_TABLE,
  ELEMENT_TODO_LI,
  ELEMENT_UL,
  MARK_BG_COLOR,
  MARK_COLOR,
  MentionElement,
  StyledElement,
  StyledLeaf,
  withPlaceholders,
  withProps,
  withStyledProps,
} from '@udecode/plate';
import * as React from 'react';
import { AiContentElement, FileElement } from 'src/features/slate-plugins';

import { BlockToolbar } from '../ui';
import CodeBlockElement from '../ui/CodeBlockElement';

export const ELEMENT_KANBAN = 'kanban';

const withStyledPlaceHolders = (components: any) =>
  withPlaceholders(components, [
    {
      key: ELEMENT_PARAGRAPH,
      placeholder: "Type '/' to open weapons",
      hideOnBlur: true,
    },
    {
      key: ELEMENT_H1,
      placeholder: 'Heading 1',
      hideOnBlur: false,
    },
    {
      key: ELEMENT_H2,
      placeholder: 'Heading 2',
      hideOnBlur: false,
    },
    {
      key: ELEMENT_H3,
      placeholder: 'Heading 3',
      hideOnBlur: false,
    },
    {
      key: ELEMENT_BLOCKQUOTE,
      placeholder: 'Empty quote',
      hideOnBlur: false,
    },
  ]);

const renderLabel = (mentionable) => {
  return `@${mentionable.name || mentionable?.data?.name}`;
};

export let components = createPlateUI({
  [ELEMENT_MENTION]: withProps(MentionElement, {
    renderLabel: renderLabel,
    styles: {
      root: {
        boxShadow: 'none',
        backgroundColor: 'inherit',
        color: '#254DDA',
        padding: '0px',
      },
    },
  }),
  [ELEMENT_AI_CONTENT]: AiContentElement,
  [ELEMENT_MICRO_TASK]: MicroTaskElement,
  [ELEMENT_MULTI_COL]: MultiColumnElement,
  [ELEMENT_COL]: ColumnElement,
  [ELEMENT_CALLOUT_TIP]: CalloutElement,
  [ELEMENT_TABLE_PLUS]: TablePlusElement,
  [ELEMENT_IMAGE]: ImageElement,
  [ELEMENT_BOARD]: BoardElement,
  [ELEMENT_H1]: withProps(StyledElement, {
    as: 'h1',
    styles: {
      root: {
        margin: '2rem 0 0.4rem',
        fontSize: '2.8rem',
        fontWeight: 600,
        lineHeight: '3.3rem',
      },
    },
  }),
  [ELEMENT_H2]: withProps(StyledElement, {
    as: 'h2',
    styles: {
      root: {
        fontWeight: 600,
        margin: '1.4rem 0 1px',
        fontSize: '2.4rem',
        lineHeight: '2.8rem',
      },
    },
  }),
  [ELEMENT_H3]: withProps(StyledElement, {
    as: 'h3',
    styles: {
      root: {
        fontWeight: 500,
        margin: '1rem 0 1px',
        fontSize: '2rem',
        lineHeight: '2.4rem',
      },
    },
  }),

  [ELEMENT_BLOCKQUOTE]: withProps(BlockquoteElement, {
    styles: {
      root: {
        borderLeft: '2px solid #878A92',
        color: '#131720',
        padding: '0px 2.6rem',
      },
    },
  }),
  [ELEMENT_TODO_LI]: withProps(TodoListElement, {
    styles: {
      checkbox: {
        width: '2rem',
        height: '2rem',
      },
      checkboxWrapper: {
        marginRight: '0.8rem',
      },
    },
  }),
  [ELEMENT_CODE_HIGHLIGHT_BLOCK]: CodeBlockElement as any,
  [ELEMENT_LINK]: LinkElement,
  [ELEMENT_KANBAN]: withProps(StyledElement, {
    as: 'h2',
    styles: {
      root: {
        fontWeight: 600,
        margin: '2.2rem 0 1px',
        fontSize: '2.4rem',
        minHeight: '6rem',
      },
    },
  }),
  [ELEMENT_LEGAL_PH]: LegalPhElement,
  [MARK_COLOR]: withStyledProps(StyledLeaf, {
    leafProps: {
      [MARK_COLOR]: ['color'],
    },
  }),
  [MARK_BG_COLOR]: withStyledProps(StyledLeaf, {
    leafProps: {
      [MARK_BG_COLOR]: ['backgroundColor'],
    },
  }),
  [MARK_COMMENT_ID]: CommentLeaf,
  [ELEMENT_EMBED]: EmbedElement,
  [ELEMENT_FILE]: FileElement,
  // Supporting the older coloring format
  'text-color': ({ children, element }: any) => (
    <span style={{ color: element.color }}>{children}</span>
  ),

  [ELEMENT_HR]: HrElement,
});

components = withStyledPlaceHolders(components);
components = withDraggables(components, [
  // {
  //   keys: [ELEMENT_UL, ELEMENT_OL],
  //   level: 0,
  // },
  {
    keys: [
      ELEMENT_PARAGRAPH,
      ELEMENT_BLOCKQUOTE,
      ELEMENT_TODO_LI,
      ELEMENT_H1,
      ELEMENT_H2,
      ELEMENT_H3,
      ELEMENT_H4,
      ELEMENT_H5,
      ELEMENT_H6,
      ELEMENT_IMAGE,
      ELEMENT_OL,
      ELEMENT_UL,
      ELEMENT_TABLE,
      ELEMENT_MEDIA_EMBED,
      ELEMENT_CODE_BLOCK,
      ELEMENT_CODE_HIGHLIGHT_BLOCK,
      ELEMENT_MICRO_TASK,
      ELEMENT_MULTI_COL,
      ELEMENT_CALLOUT_TIP,
      ELEMENT_TABLE_PLUS,
      ELEMENT_KANBAN,
      ELEMENT_AI_CONTENT,
      ELEMENT_BOARD,
      ELEMENT_EMBED,
      ELEMENT_HR,
      ELEMENT_FILE,
      ELEMENT_DATE_TODAY,
    ],
    onRenderDragHandle: BlockToolbar as any,
  },
  {
    key: ELEMENT_H1,
    styles: {
      gutterLeft: {
        paddingTop: '8px',
      },
    },
  },
  {
    key: ELEMENT_H2,
    styles: {
      gutterLeft: {
        paddingTop: '0.5rem',
      },
      blockToolbarWrapper: {
        height: '0.8em',
      },
    },
  },
  {
    key: ELEMENT_H3,
    styles: {
      blockToolbarWrapper: {
        height: '0.8em',
      },
      gutterLeft: {
        paddingTop: '2px',
      },
    },
  },
  {
    key: ELEMENT_MICRO_TASK,
    styles: {
      gutterLeft: {
        paddingTop: '1.8rem',
      },
      blockToolbarWrapper: {
        height: '1.3em',
      },
    },
  },
  {
    key: ELEMENT_MULTI_COL,
    styles: {
      gutterLeft: {
        paddingTop: '1rem',
      },
      blockToolbarWrapper: {
        height: '1.3em',
      },
    },
  },
  {
    key: ELEMENT_TODO_LI,
    styles: {
      gutterLeft: {
        paddingTop: '1rem',
      },
    },
  },
  {
    key: ELEMENT_TABLE_PLUS,
    styles: {
      blockToolbarWrapper: {
        height: '1.3em',
      },
    },
  },
  {
    key: ELEMENT_HR,
    styles: {
      blockToolbarWrapper: {
        height: '1.3em',
      },
    },
  },
  {
    keys: [ELEMENT_H4, ELEMENT_H5, ELEMENT_H6],
    styles: {
      blockToolbarWrapper: {
        height: '1.3em',
      },
    },
  },
  {
    keys: [ELEMENT_PARAGRAPH, ELEMENT_UL, ELEMENT_OL],
    styles: {
      gutterLeft: {
        padding: '4px 0 0',
      },
    },
  },
]);
